import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CampaignIcon from '@mui/icons-material/Campaign';
import HubIcon from '@mui/icons-material/Hub';
import ChatIcon from '@mui/icons-material/Chat';
import { CHAT_ENABLED } from '../constants/featureFlags';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import {
  BOTTOM_NAVIGATION_LOADED, BOTTOM_NAVIGATION_SET_POINTS,
  BOTTOM_NAVIGATION_SET_UNREAD_MESSAGES,
  BOTTOM_NAVIGATION_UNLOADED,
  LOGOUT,
  TOP_NAVIGATION_VISIBILITY
} from '../constants/actionTypes';
import { useEffect, useMemo } from 'react';
import agent from '../agent';

import { Badge as BaseBadge, badgeClasses } from '@mui/base/Badge';
import { styled } from '@mui/system';
import { DEFAULT_THEME_COLOR, DEFAULT_GRADIENT } from '../constants/theme';
import { getFreakIcon } from '../utils/freakUtils';
import { useAuth0 } from '@auth0/auth0-react';

const Badge = styled(BaseBadge)(
  () => `
  box-sizing: border-box;
  position: relative;
  display: flex;
  align-self: center;

  & .${badgeClasses.badge} {
    z-index: auto;
    position: absolute;
    top: 0;
    right: 0;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    color: #fff;
    font-weight: 700;
    font-size: 10px;
    line-height: 18px;
    white-space: nowrap;
    text-align: center;
    border-radius: 9px;
    background: ${DEFAULT_GRADIENT};
    transform: translate(50%, -50%);
    transform-origin: 100% 0;
  }

  & .${badgeClasses.invisible} {
    opacity: 0;
    pointer-events: none;
  }
  `,
);

const NAV_ITEMS = [
  { path: ['/freak', '/'], index: 0 },
  { path: ['/notifications'], index: 1 },
  { path: ['/places'], index: 2 },
  { path: ['/freak-hub'], index: 3 },
  { path: ['/campaigns'], index: 4 },
  { path: ['/chat'], index: 5 },
  { path: ['/blasts'], index: 6 },
];

const ROUTES = [
  '/freak',
  '/notifications',
  '/places',
  '/freak-hub',
  '/campaigns',
  '/chat',
  '/blasts',
];

const NavBtn = styled('button')(({ active }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '6px 2px',
  outline: 'none',
  WebkitTapHighlightColor: 'transparent',
  color: active ? DEFAULT_THEME_COLOR : 'rgba(255,255,255,0.45)',
  transition: 'color 0.18s ease',
  position: 'relative',
  '&:focus': { outline: 'none' },
  '& svg': {
    fontSize: 22,
    filter: active
      ? `drop-shadow(0 0 6px ${DEFAULT_THEME_COLOR}88)`
      : 'none',
    transition: 'filter 0.18s ease, transform 0.15s ease',
    transform: active ? 'translateY(-1px)' : 'none',
  },
}));

const ActiveDot = styled('span')({
  position: 'absolute',
  bottom: 2,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 4,
  height: 4,
  borderRadius: '50%',
  background: DEFAULT_THEME_COLOR,
  boxShadow: `0 0 6px ${DEFAULT_THEME_COLOR}`,
});

const FixedBottomNavigation = (props) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const device = props.device;
  const context = props.context || props?.currentUser?.context || 'wof';
  const notificationsCount = props.common?.notificationsCount || 0;

  const activeIndex = useMemo(() => {
    const match = NAV_ITEMS.find(item =>
      item.path.some(p => location.pathname === p)
    );
    return match ? match.index : -1;
  }, [location.pathname]);

  const fetchNotificationsCount = (p) => {
    if (isAuthenticated) {
      p.onLoad(Promise.all([agent.Notifications.count()]));
    }
  };

  const fetchUserFreakPoints = (p) => {
    if (isAuthenticated) {
      p.onLoadPoints(Promise.all([agent.Points.count()]));
    }
  };

  useEffect(() => {
    fetchNotificationsCount(props);
    fetchUserFreakPoints(props);
  }, []);

  const go = (index) => {
    fetchNotificationsCount(props);
    fetchUserFreakPoints(props);
    const route = ROUTES[index];
    if (route) {
      isAuthenticated ? navigate(route) : navigate('/login');
    }
  };

  if (!props?.bottomNavigationVisible) return null;

  const barHeight = device === 'desktop' ? 50 : 58;

  return (
    <Box sx={{ pb: `${barHeight}px` }} ref={ref}>
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'linear-gradient(135deg, #0d1b2a 0%, #0d3d2e 100%)',
          borderTop: '1px solid rgba(255,255,255,0.09)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        elevation={14}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            height: barHeight,
            width: '100%',
          }}
        >
          <NavBtn active={activeIndex === 0 ? 1 : 0} onClick={() => go(0)}>
            {getFreakIcon(context)}
            {activeIndex === 0 && <ActiveDot />}
          </NavBtn>

          <NavBtn active={activeIndex === 1 ? 1 : 0} onClick={() => go(1)}>
            <Badge badgeContent={notificationsCount} color="primary">
              <NotificationsNoneIcon />
            </Badge>
            {activeIndex === 1 && <ActiveDot />}
          </NavBtn>

          <NavBtn active={activeIndex === 2 ? 1 : 0} onClick={() => go(2)}>
            <TravelExploreIcon />
            {activeIndex === 2 && <ActiveDot />}
          </NavBtn>

          <NavBtn active={activeIndex === 3 ? 1 : 0} onClick={() => go(3)}>
            <HubIcon />
            {activeIndex === 3 && <ActiveDot />}
          </NavBtn>

          <NavBtn active={activeIndex === 4 ? 1 : 0} onClick={() => go(4)}>
            <CampaignIcon />
            {activeIndex === 4 && <ActiveDot />}
          </NavBtn>

          {CHAT_ENABLED && (
            <NavBtn active={activeIndex === 5 ? 1 : 0} onClick={() => go(5)}>
              <ChatIcon />
              {activeIndex === 5 && <ActiveDot />}
            </NavBtn>
          )}

          <NavBtn active={activeIndex === 6 ? 1 : 0} onClick={() => go(6)}>
            <LocalFireDepartmentIcon />
            {activeIndex === 6 && <ActiveDot />}
          </NavBtn>
        </Box>
      </Paper>
    </Box>
  );
};

const mapStateToProps = state => ({
  ...state,
  bottomNavigationVisible: state.common.bottomNavigationVisible,
  device: state.common.device,
  currentUser: state.common.currentUser,
  context: state.common.context,
});

const mapDispatchToProps = dispatch => ({
  onClickLogout: () => dispatch({ type: LOGOUT }),
  onLoad: payload =>
    dispatch({ type: BOTTOM_NAVIGATION_LOADED, payload }),
  onLoadPoints: payload =>
    dispatch({ type: BOTTOM_NAVIGATION_SET_POINTS, payload }),
  setUnreadMessages: payload =>
    dispatch({ type: BOTTOM_NAVIGATION_SET_UNREAD_MESSAGES, payload }),
  onUnload: () =>
    dispatch({ type: BOTTOM_NAVIGATION_UNLOADED }),
  setTopNavigationVisible: isVisible => dispatch({
    type: TOP_NAVIGATION_VISIBILITY,
    payload: isVisible,
  }),
});

export default connect(mapStateToProps, mapDispatchToProps)(FixedBottomNavigation);
